import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Ban,
  RotateCcw,
  LayoutGrid,
  Table2,
  User,
  Languages,
  MessageSquare,
} from "lucide-react";
import {
  listVolunteers,
  createVolunteer,
  updateVolunteer,
  deleteVolunteer,
  setVolunteerDeactivated,
  type VolunteerWrite,
} from "@/lib/volunteers-api";
import { sanitizePhone, isValidPhone, PHONE_INPUT_PROPS } from "@/lib/phone";

/** Same chip set as the public /volunteer form. */
const LANGUAGE_OPTIONS = ["Gujarati", "Hindi", "English", "Marwari", "Marathi", "Sanskrit"];

type V = {
  id?: string;
  name: string;
  email: string;
  phone: string;
  languages: string[];
  experience_years: number;
  charges_per_day: number;
  bio_en: string;
  bio_hi: string;
  bio_gu: string;
  active: boolean;
  /** Read-only: set when the volunteer self-deactivated their account. */
  is_deactivated: boolean;
  /** Read-only, server-computed from bookings — shown but never sent. */
  is_booked: boolean;
  is_booked_date: string[];
  /** Read-only until the backend adds them. */
  area: string;
  gender: string;
};
const EMPTY: V = {
  name: "",
  email: "",
  phone: "",
  languages: ["English"],
  experience_years: 0,
  charges_per_day: 500,
  bio_en: "",
  bio_hi: "",
  bio_gu: "",
  active: true,
  is_deactivated: false,
  is_booked: false,
  is_booked_date: [],
  area: "",
  gender: "",
};

/**
 * A self-deactivated volunteer (DELETE /volunteer/me/) vs a brand-new applicant.
 * The backend exposes this directly via `is_deactivated`.
 */
function isDeactivated(v: V): boolean {
  return v.is_deactivated;
}

export const Route = createFileRoute("/admin/volunteers")({ component: VolPage });

function VolPage() {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<V | null>(null);
  // Homepage applications land as active=false → "Pending". Admin approves (active=true) or rejects.
  // A self- or admin-deactivated volunteer (is_deactivated=true) sits in its own tab.
  const [tab, setTab] = useState<"pending" | "approved" | "deactivated">("pending");
  const [view, setView] = useState<"cards" | "table">("cards");

  const {
    data: list = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["jgy-volunteers"],
    queryFn: listVolunteers,
  });

  // Three mutually-exclusive buckets. is_deactivated wins over the plain active flag.
  const deactivated = list.filter((v) => v.is_deactivated);
  const pending = list.filter((v) => !v.active && !v.is_deactivated);
  const approved = list.filter((v) => v.active && !v.is_deactivated);
  const rows = tab === "pending" ? pending : tab === "approved" ? approved : deactivated;

  const toWrite = (d: V): VolunteerWrite => ({
    name: d.name,
    email: d.email,
    phone: d.phone,
    languages: d.languages,
    experience_years: d.experience_years,
    charges_per_day: d.charges_per_day,
    bio_en: d.bio_en,
    bio_hi: d.bio_hi,
    bio_gu: d.bio_gu,
    active: d.active,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["jgy-volunteers"] });
  const onError = (e: unknown) =>
    toast.error(e instanceof Error ? e.message : "Something went wrong");

  const createMut = useMutation({
    mutationFn: (d: V) => createVolunteer(toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Volunteer created");
      setEdit(null);
    },
    onError,
  });
  const updateMut = useMutation({
    mutationFn: (d: V) => updateVolunteer(d.id as string, toWrite(d)),
    onSuccess: () => {
      invalidate();
      toast.success("Volunteer updated");
      setEdit(null);
    },
    onError,
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteVolunteer(id),
    onSuccess: () => {
      invalidate();
      toast.success("Deleted");
    },
    onError,
  });
  const approveMut = useMutation({
    mutationFn: (d: V) => updateVolunteer(d.id as string, toWrite({ ...d, active: true })),
    onSuccess: () => {
      invalidate();
      toast.success("Application approved");
    },
    onError,
  });
  const rejectMut = useMutation({
    mutationFn: (d: V) => updateVolunteer(d.id as string, toWrite({ ...d, active: false })),
    onSuccess: () => {
      invalidate();
      toast.success("Application rejected");
    },
    onError,
  });
  const deactivateMut = useMutation({
    mutationFn: (d: V) => setVolunteerDeactivated(d.id as string, true),
    onSuccess: () => {
      invalidate();
      toast.success("Volunteer deactivated");
    },
    onError,
  });
  const restoreMut = useMutation({
    mutationFn: (d: V) => setVolunteerDeactivated(d.id as string, false),
    onSuccess: () => {
      invalidate();
      toast.success("Volunteer account restored");
    },
    onError,
  });

  const saving = createMut.isPending || updateMut.isPending;

  const save = () => {
    if (!edit) {
      return;
    }
    if (!isValidPhone(edit.phone)) {
      toast.error("Enter a valid 10-digit phone number");
      return;
    }
    if (edit.id) {
      updateMut.mutate(edit);
    } else {
      createMut.mutate(edit);
    }
  };
  const del = (id: string) => {
    if (confirm("Delete?")) {
      deleteMut.mutate(id);
    }
  };
  const approve = (v: V) => approveMut.mutate(v);
  // Reject just sets active=false (keeps the record) — no deletion.
  const reject = (v: V) => rejectMut.mutate(v);
  const deactivate = (v: V) => deactivateMut.mutate(v);
  const restore = (v: V) => restoreMut.mutate(v);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-2xl font-bold">Volunteers</h2>
          <p className="text-sm text-muted-foreground">Yatra guide volunteers</p>
        </div>
        <button onClick={() => setEdit({ ...EMPTY })} className="btn-primary py-2 text-sm">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-border bg-card p-1 text-sm">
          {(
            [
              { key: "pending", label: "Pending", count: pending.length },
              { key: "approved", label: "Approved", count: approved.length },
              { key: "deactivated", label: "Deactivated", count: deactivated.length },
            ] as const
          ).map((tp) => (
            <button
              key={tp.key}
              onClick={() => setTab(tp.key)}
              className={`rounded-full px-4 py-1.5 font-semibold transition-colors ${
                tab === tp.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tp.label}
              <span
                className={`ml-1.5 rounded-full px-1.5 text-[10px] ${
                  tab === tp.key ? "bg-primary-foreground/20" : "bg-muted"
                }`}
              >
                {tp.count}
              </span>
            </button>
          ))}
        </div>

        {/* Both views are wired; keep whichever reads better. */}
        <div className="inline-flex rounded-full border border-border bg-card p-1">
          {(
            [
              { key: "cards", label: "Cards", Icon: LayoutGrid },
              { key: "table", label: "Table", Icon: Table2 },
            ] as const
          ).map((vw) => (
            <button
              key={vw.key}
              onClick={() => setView(vw.key)}
              title={`${vw.label} view`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                view === vw.key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <vw.Icon className="h-3.5 w-3.5" />
              {vw.label}
            </button>
          ))}
        </div>
      </div>
      {view === "cards" ? (
        <VolunteerCards
          rows={rows}
          isLoading={isLoading}
          isError={isError}
          error={error}
          tab={tab}
          onEdit={setEdit}
          onApprove={approve}
          onDeactivate={deactivate}
          onRestore={restore}
          onDelete={del}
          busy={
            approveMut.isPending ||
            rejectMut.isPending ||
            deactivateMut.isPending ||
            restoreMut.isPending
          }
        />
      ) : (
        <div className="-mx-4 overflow-hidden rounded-2xl border border-border bg-card sm:mx-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="p-3">Name</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3">Languages</th>
                  <th className="p-3">Experience</th>
                  <th className="p-3">Charges/day</th>
                  <th className="p-3">Booked</th>
                  <th className="p-3">Status</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                      Loading volunteers…
                    </td>
                  </tr>
                )}
                {isError && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-sm text-destructive">
                      {error instanceof Error ? error.message : "Failed to load volunteers"}
                    </td>
                  </tr>
                )}
                {!isLoading && !isError && rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                      {tab === "pending"
                        ? "No pending applications"
                        : tab === "approved"
                          ? "No approved volunteers yet"
                          : "No deactivated volunteers"}
                    </td>
                  </tr>
                )}
                {rows.map((v) => (
                  <tr key={v.id} className="border-t border-border hover:bg-muted/40">
                    <td className="p-3">
                      <div className="font-semibold">{v.name}</div>
                      {v.email ? (
                        <div className="text-[10px] text-muted-foreground">{v.email}</div>
                      ) : null}
                    </td>
                    <td className="p-3 text-muted-foreground">{v.phone || "—"}</td>
                    <td className="p-3 text-muted-foreground">{v.languages?.join(", ") || "—"}</td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      {v.experience_years} yrs
                    </td>
                    <td className="p-3 text-muted-foreground whitespace-nowrap">
                      ₹{v.charges_per_day}
                    </td>
                    <td className="p-3">
                      {v.is_booked_date.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {v.is_booked_date.map((d) => (
                            <span
                              key={d}
                              className="rounded-full bg-muted px-2 py-0.5 text-[10px] whitespace-nowrap"
                            >
                              {d}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          v.active
                            ? "bg-green-100 text-green-700"
                            : isDeactivated(v)
                              ? "bg-amber-100 text-amber-700"
                              : "bg-primary/12 text-primary"
                        }`}
                      >
                        {v.active ? "Active" : isDeactivated(v) ? "Deactivated" : "Pending"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1">
                        {tab === "pending" ? (
                          <>
                            <button
                              onClick={() => approve(v)}
                              disabled={approveMut.isPending}
                              title="Approve"
                              className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
                            >
                              <Check className="h-3.5 w-3.5" /> Approve
                            </button>
                            <button
                              onClick={() => reject(v)}
                              disabled={rejectMut.isPending}
                              title="Reject"
                              className="inline-flex items-center gap-1 rounded-lg border border-destructive/30 px-2 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60"
                            >
                              <X className="h-3.5 w-3.5" /> Reject
                            </button>
                            <button
                              onClick={() => setEdit(v)}
                              title="View / Edit"
                              className="rounded-lg p-1.5 hover:bg-muted"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            {tab === "approved" ? (
                              <button
                                onClick={() => deactivate(v)}
                                disabled={deactivateMut.isPending}
                                title="Deactivate account"
                                className="inline-flex items-center gap-1 rounded-lg border border-destructive/30 px-2 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60"
                              >
                                <Ban className="h-3.5 w-3.5" /> Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => restore(v)}
                                disabled={restoreMut.isPending}
                                title="Restore account"
                                className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/10 px-2 py-1 text-xs font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
                              >
                                <RotateCcw className="h-3.5 w-3.5" /> Restore
                              </button>
                            )}
                            <button
                              onClick={() => setEdit(v)}
                              title="Edit"
                              className="rounded-lg p-1.5 hover:bg-muted"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => v.id && del(v.id)}
                              title="Delete"
                              className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {edit && (
        <Editor
          v={edit}
          setV={setEdit}
          onSave={save}
          onClose={() => setEdit(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function VolunteerCards({
  rows,
  isLoading,
  isError,
  error,
  tab,
  onEdit,
  onApprove,
  onDeactivate,
  onRestore,
  onDelete,
  busy,
}: {
  rows: V[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  tab: "pending" | "approved" | "deactivated";
  onEdit: (v: V) => void;
  onApprove: (v: V) => void;
  onDeactivate: (v: V) => void;
  onRestore: (v: V) => void;
  onDelete: (id: string) => void;
  busy: boolean;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-52 animate-pulse rounded-2xl border border-border bg-card" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-destructive">
        {error instanceof Error ? error.message : "Failed to load volunteers"}
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
        {tab === "pending"
          ? "No pending applications"
          : tab === "approved"
            ? "No approved volunteers yet"
            : "No deactivated volunteers"}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      {rows.map((v) => {
        // area/gender only render once the backend exposes them.
        const meta = [
          v.area ? `Area: ${v.area}` : null,
          v.gender ? `Gender: ${v.gender}` : null,
        ].filter(Boolean);
        return (
          <div
            key={v.id}
            className="flex flex-col rounded-2xl border border-border bg-card p-5 transition-shadow hover:shadow-[var(--shadow-soft)]"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-serif text-lg font-bold leading-tight">{v.name}</h3>
                <div className="mt-0.5 text-xs text-muted-foreground">{v.phone || "—"}</div>
                {v.email ? <div className="text-xs text-muted-foreground">{v.email}</div> : null}
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  v.active
                    ? "bg-green-100 text-green-700"
                    : isDeactivated(v)
                      ? "bg-amber-100 text-amber-700"
                      : "bg-primary/12 text-primary"
                }`}
              >
                {v.active ? "Approved" : isDeactivated(v) ? "Deactivated" : "Pending"}
              </span>
            </div>

            <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
              {meta.length > 0 ? <div>{meta.join(" · ")}</div> : null}
              <div>Languages: {v.languages?.join(", ") || "—"}</div>
              <div>
                Experience: {v.experience_years} yrs · ₹{v.charges_per_day}/day
              </div>
              {v.is_booked_date.length > 0 ? (
                <div>Booked: {v.is_booked_date.join(", ")}</div>
              ) : null}
            </div>

            {v.bio_en ? <p className="mt-3 text-sm text-foreground">{v.bio_en}</p> : null}

            <div className="mt-auto flex items-center justify-between gap-2 pt-4">
              {isDeactivated(v) ? (
                <button
                  onClick={() => onRestore(v)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Restore
                </button>
              ) : v.active ? (
                <button
                  onClick={() => onDeactivate(v)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-full border border-destructive/30 px-3 py-1 text-xs font-semibold text-destructive hover:bg-destructive/10 disabled:opacity-60"
                >
                  <Ban className="h-3.5 w-3.5" /> Deactivate
                </button>
              ) : (
                <button
                  onClick={() => onApprove(v)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary hover:bg-primary/15 disabled:opacity-60"
                >
                  <Check className="h-3.5 w-3.5" /> Approve
                </button>
              )}
              <div className="flex gap-1">
                <button
                  onClick={() => onEdit(v)}
                  title="Edit"
                  className="rounded-lg p-1.5 hover:bg-muted"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => v.id && onDelete(v.id)}
                  title="Delete"
                  className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Editor({
  v,
  setV,
  onSave,
  onClose,
  saving,
}: {
  v: V;
  setV: (x: V) => void;
  onSave: () => void;
  onClose: () => void;
  saving: boolean;
}) {
  const u = (k: keyof V, val: any) => setV({ ...v, [k]: val });
  const toggleLang = (l: string) =>
    setV({
      ...v,
      languages: v.languages.includes(l) ? v.languages.filter((x) => x !== l) : [...v.languages, l],
    });
  // Keep any language already on the record that isn't one of the standard chips, so editing
  // never silently drops a value the API returned.
  const langOptions = [
    ...LANGUAGE_OPTIONS,
    ...v.languages.filter((l) => !LANGUAGE_OPTIONS.includes(l)),
  ];

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto jgy-overlay-scrim p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="mx-auto my-8 w-full max-w-2xl rounded-2xl bg-card p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold">{v.id ? "Edit" : "Add"} Volunteer</h3>
          <button onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Personal details */}
        <SectionHead icon={User} title="Personal details" />
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F label="Full Name" required>
            <input
              className="inp"
              value={v.name}
              onChange={(e) => u("name", e.target.value)}
              placeholder="Ramesh Patel"
            />
          </F>
          <F label="Mobile Number" required>
            <input
              {...PHONE_INPUT_PROPS}
              required
              className="inp"
              value={v.phone}
              onChange={(e) => u("phone", sanitizePhone(e.target.value))}
            />
          </F>
          <F label="Email" full>
            <input
              type="email"
              className="inp"
              placeholder="you@example.com"
              value={v.email}
              onChange={(e) => u("email", e.target.value)}
            />
          </F>
          <F label="Years of Experience">
            <input
              type="number"
              min={0}
              className="inp"
              value={v.experience_years}
              onChange={(e) => u("experience_years", +e.target.value)}
            />
          </F>
          <F label="Charges per day (₹)">
            <input
              type="number"
              min={0}
              className="inp"
              value={v.charges_per_day}
              onChange={(e) => u("charges_per_day", +e.target.value)}
            />
          </F>
          {/* Admin-only: applicants can never set this from the public form. */}
          <F label="Active">
            <select
              className="inp"
              value={String(v.active)}
              onChange={(e) => u("active", e.target.value === "true")}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </F>
        </div>

        <Divider />

        {/* Languages */}
        <SectionHead icon={Languages} title="Languages you speak" required />
        <div className="mt-3 flex flex-wrap gap-2">
          {langOptions.map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => toggleLang(l)}
              className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                v.languages.includes(l)
                  ? "border-primary bg-primary/15 text-primary"
                  : "border-border bg-background text-muted-foreground hover:bg-muted"
              }`}
            >
              {l}
            </button>
          ))}
        </div>

        <Divider />

        {/* Bios */}
        <SectionHead icon={MessageSquare} title="A few words about you" />
        <div className="mt-3 space-y-3">
          <F label="Short Bio (English)" full>
            <textarea
              rows={3}
              className="inp"
              value={v.bio_en}
              onChange={(e) => u("bio_en", e.target.value)}
              placeholder="Your background, temples you know well, etc."
            />
          </F>
          <F label="Short Bio (हिन्दी)" full>
            <textarea
              rows={3}
              className="inp"
              value={v.bio_hi}
              onChange={(e) => u("bio_hi", e.target.value)}
            />
          </F>
          <F label="Short Bio (ગુજરાતી)" full>
            <textarea
              rows={3}
              className="inp"
              value={v.bio_gu}
              onChange={(e) => u("bio_gu", e.target.value)}
            />
          </F>
        </div>

        {/* Booking state is derived from bookings server-side, so it's shown read-only. */}
        {v.id ? (
          <div className="mt-5 rounded-xl bg-muted/50 p-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-semibold uppercase text-muted-foreground">
                Booking status
              </span>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  v.is_booked ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                }`}
              >
                {v.is_booked ? "Booked" : "Not booked"}
              </span>
              <span className="text-[10px] text-muted-foreground">(read-only)</span>
            </div>
            {v.is_booked_date.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {v.is_booked_date.map((d) => (
                  <span key={d} className="rounded-full bg-card px-2 py-0.5 text-[11px]">
                    {d}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-[11px] text-muted-foreground">No confirmed bookings</p>
            )}
          </div>
        ) : null}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="btn-outline py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="btn-primary py-2 text-sm disabled:pointer-events-none disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <style>{`.inp{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:.75rem;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:hsl(var(--primary))}`}</style>
    </div>
  );
}

function SectionHead({
  icon: Icon,
  title,
  required,
}: {
  icon: typeof User;
  title: string;
  required?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-primary" />
      <h4 className="font-serif text-base font-bold text-foreground">
        {title}
        {required ? <span className="text-destructive"> *</span> : null}
      </h4>
    </div>
  );
}

function Divider() {
  return <div className="my-6 border-t border-dashed border-border" />;
}

function F({
  label,
  full,
  required,
  children,
}: {
  label: string;
  full?: boolean;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
